import { Context, useContext } from 'react';

import {
  ImageEntity,
  TextSubtext,
  CityServices,
  CityService,
  FareEstimateContent,
} from '../../shared/cities/domain/entities/CitiesEntity';

// `ModalProvider.mjs` (wired in `_app`) writes to the React.Context exported
// from the `.mjs` file; `ModalManager.mjs` reads from the same context to
// render the active modal. If this `.tsx` calls `createContext` again, TS
// `toggleModal` updates a separate context that the `.mjs` renderer never
// observes — clicks become no-ops. Re-use the `.mjs` context so all consumers
// subscribe to the same one. (Same pattern as `AuthContextV1.tsx` and
// `CityContext.tsx`.)
import { context as mjsModalContext } from './ModalContext.mjs';

// TODO: Replace with TypeScript discriminated union once ModalManager.res is migrated.
// e.g. type ModalVariant = { type: 'CitySelector'; data: CityServices[]; ... } | { type: 'FareEstimate'; ... }
// Current shape matches compiled ReScript Modals.t variant — do not use directly, use factory functions below.
interface ModalVariant {
  TAG: number;
  _0: unknown;
  _1?: unknown;
  _2?: unknown;
  _3?: unknown;
}

export interface ModalContextType {
  modal: ModalVariant | undefined;
  toggleModal: (modal: ModalVariant | undefined) => void;
}

const context = mjsModalContext as Context<ModalContextType>;

export function useModalContext(): ModalContextType {
  return useContext(context);
}

export { context as ModalContextRaw };

// --- Modal factory functions ---
// These create the correct variant shape for the ReScript modal renderer
// (ModalManager.res) without exposing TAG numbers to consumers.

export function openCitySelectorModal(
  cityData: CityServices[],
  closeIcon: ImageEntity,
  cityTitleDesc: TextSubtext,
): ModalVariant {
  return { TAG: 7, _0: true, _1: cityData, _2: closeIcon, _3: cityTitleDesc };
}

export function openFareEstimateModal(
  categoryCards: CityService[],
  fareEstimateFormContent: unknown,
  modalContent: FareEstimateContent,
): ModalVariant {
  return {
    TAG: 9,
    _0: true,
    _1: categoryCards,
    _2: fareEstimateFormContent,
    _3: modalContent,
  };
}

// PaymentStatusModal(bool, PaymentStatus.t, BookingFlow.t) — TAG 17.
// PaymentStatus.t: PaymentSuccess => 0, Paymentfailure(amount) => { TAG: 0, _0 }.
// BookingFlow.t: PnM => 0, Courier => 1.
export function openPaymentSuccessModal(): ModalVariant {
  return { TAG: 17, _0: true, _1: 0, _2: 0 };
}

export function openPaymentFailureModal(amount: string): ModalVariant {
  return { TAG: 17, _0: true, _1: { TAG: 0, _0: amount }, _2: 0 };
}
